import React, { useState, useMemo, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Search, ChevronRight, ChevronDown, Book, HelpCircle } from 'lucide-react';
import { Input } from './Input';
import styles from './FaqPage.module.css';

interface FaqSection {
  id: string;
  title: string;
  questions: FaqQuestion[];
}

interface FaqQuestion {
  id: string;
  question: string;
  answer: string;
}

// Parse the FAQ markdown into structured data
function parseFaqMarkdown(markdown: string): FaqSection[] {
  const sections: FaqSection[] = [];
  const lines = markdown.split('\n');

  let currentSection: FaqSection | null = null;
  let currentQuestion: FaqQuestion | null = null;
  let answerLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // H2 headers are main sections
    if (line.startsWith('## ')) {
      // Save previous question if exists
      if (currentQuestion && currentSection) {
        currentQuestion.answer = answerLines.join('\n').trim();
        currentSection.questions.push(currentQuestion);
        currentQuestion = null;
        answerLines = [];
      }

      // Save previous section if exists
      if (currentSection) {
        sections.push(currentSection);
      }

      // Start new section
      const title = line.replace('## ', '').trim();
      currentSection = {
        id: title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        title,
        questions: [],
      };
    }
    // H3 headers are questions
    else if (line.startsWith('### ')) {
      // Save previous question if exists
      if (currentQuestion && currentSection) {
        currentQuestion.answer = answerLines.join('\n').trim();
        currentSection.questions.push(currentQuestion);
        answerLines = [];
      }

      // Start new question
      const question = line.replace('### ', '').trim();
      currentQuestion = {
        id: question.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        question,
        answer: '',
      };
    }
    // Content lines are part of the answer
    else if (currentQuestion && line.trim()) {
      answerLines.push(line);
    }
  }

  // Save last question and section
  if (currentQuestion && currentSection) {
    currentQuestion.answer = answerLines.join('\n').trim();
    currentSection.questions.push(currentQuestion);
  }
  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
}

export const FaqPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [expandedNavSections, setExpandedNavSections] = useState<Set<string>>(new Set());
  const [faqData, setFaqData] = useState<FaqSection[]>([]);

  // Load FAQ markdown
  useEffect(() => {
    fetch('/docs/FAQ.md')
      .then((res) => res.text())
      .then((markdown) => {
        const sections = parseFaqMarkdown(markdown);
        setFaqData(sections);
        // Expand first section by default
        if (sections.length > 0) {
          setExpandedSections(new Set([sections[0].id]));
          setExpandedNavSections(new Set([sections[0].id]));
        }
      })
      .catch((err) => console.error('Failed to load FAQ:', err));
  }, []);

  // Filter sections and questions based on search
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return faqData;

    const query = searchQuery.toLowerCase();
    return faqData
      .map((section) => ({
        ...section,
        questions: section.questions.filter(
          (q) => q.question.toLowerCase().includes(query) || q.answer.toLowerCase().includes(query),
        ),
      }))
      .filter((section) => section.questions.length > 0);
  }, [faqData, searchQuery]);

  // Auto-expand sections with search results
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedSections(new Set(filteredSections.map((s) => s.id)));
      setExpandedQuestions(new Set(filteredSections.flatMap((s) => s.questions.map((q) => q.id))));
      setExpandedNavSections(new Set(filteredSections.map((s) => s.id)));
    }
  }, [searchQuery, filteredSections]);

  const toggleNavSection = (sectionId: string) => {
    setExpandedNavSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedSections((prev) => new Set(prev).add(sectionId));
    }
  };

  return (
    <div className={styles.container}>
      {/* Page Title Section */}
      <div className={styles.pageTitle}>
        <div className={styles.pageTitleContent}>
          <div className={styles.pageTitleIcon}>
            <Book size={32} />
          </div>
          <div>
            <h1 className={styles.title}>Documentation</h1>
            <p className={styles.subtitle}>Everything you need to know about Tasking.tech</p>
          </div>
        </div>
      </div>

      <div className={styles.layout}>
        {/* Sidebar Navigation */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarContent}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} size={16} />
              <Input
                id="faq-search"
                name="faq-search"
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            <nav className={styles.nav}>
              {faqData.map((section) => {
                const isExpanded = expandedNavSections.has(section.id);
                return (
                  <div key={section.id} className={styles.navGroup}>
                    <button
                      className={styles.navItem}
                      onClick={() => {
                        toggleNavSection(section.id);
                        scrollToSection(section.id);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDown size={14} className={styles.navIcon} />
                      ) : (
                        <ChevronRight size={14} className={styles.navIcon} />
                      )}
                      <span>{section.title}</span>
                    </button>
                    {isExpanded && section.questions.length > 0 && (
                      <div className={styles.navSubmenu}>
                        {section.questions.map((q) => (
                          <button
                            key={q.id}
                            className={styles.navSubItem}
                            onClick={() => {
                              scrollToSection(section.id);
                              setExpandedSections((prev) => new Set(prev).add(section.id));
                              setExpandedQuestions((prev) => new Set(prev).add(q.id));
                              setTimeout(() => {
                                const el = document.getElementById(`question-${q.id}`);
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }, 100);
                            }}
                          >
                            <span className={styles.navSubDot} />
                            <span>{q.question}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className={styles.main}>
          {filteredSections.length === 0 ? (
            <div className={styles.emptyState}>
              <HelpCircle size={48} className={styles.emptyIcon} />
              <h3>No results found</h3>
              <p>Try adjusting your search query</p>
            </div>
          ) : (
            <div className={styles.content}>
              {filteredSections.map((section) => (
                <section key={section.id} id={`section-${section.id}`} className={styles.section}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={styles.sectionHeader}
                  >
                    {expandedSections.has(section.id) ? (
                      <ChevronDown size={20} className={styles.sectionIcon} />
                    ) : (
                      <ChevronRight size={20} className={styles.sectionIcon} />
                    )}
                    <h2 className={styles.sectionTitle}>{section.title}</h2>
                  </button>

                  {expandedSections.has(section.id) && (
                    <div className={styles.questions}>
                      {section.questions.map((question) => (
                        <div
                          key={question.id}
                          id={`question-${question.id}`}
                          className={styles.question}
                        >
                          <button
                            onClick={() => toggleQuestion(question.id)}
                            className={styles.questionHeader}
                          >
                            <span className={styles.questionText}>{question.question}</span>
                            {expandedQuestions.has(question.id) ? (
                              <ChevronDown size={18} className={styles.questionIcon} />
                            ) : (
                              <ChevronRight size={18} className={styles.questionIcon} />
                            )}
                          </button>

                          {expandedQuestions.has(question.id) && (
                            <div
                              className={styles.answer}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(formatAnswer(question.answer)),
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// Simple markdown-to-HTML formatter
function formatAnswer(markdown: string): string {
  let html = markdown;

  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Inline code
  html = html.replace(/`(.+?)`/g, '<code>$1</code>');

  // Links (block javascript: protocol)
  html = html.replace(/\[(.+?)\]\((.+?)\)/g, (_match, text, url) => {
    if (/^\s*javascript:/i.test(url)) return text;
    return `<a href="${url}" rel="noopener noreferrer">${text}</a>`;
  });

  // Markdown tables
  const lines = html.split('\n');
  const outputLines: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const nextLine = lines[i + 1];
    const hasPipes = line.includes('|');
    const nextIsSeparator =
      typeof nextLine === 'string' &&
      /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/.test(nextLine);

    if (hasPipes && nextIsSeparator) {
      const parseTableRow = (row: string): string[] =>
        row
          .trim()
          .replace(/^\|/, '')
          .replace(/\|$/, '')
          .split('|')
          .map((cell) => cell.trim());

      const headerCells = parseTableRow(line);
      const tableRows: string[][] = [];
      i += 2; // Skip header + separator

      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        tableRows.push(parseTableRow(lines[i]));
        i++;
      }

      const headerHtml = headerCells.map((cell) => `<th>${cell}</th>`).join('');
      const bodyHtml = tableRows
        .map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join('')}</tr>`)
        .join('');
      outputLines.push(
        `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`,
      );
      continue;
    }

    outputLines.push(line);
    i++;
  }
  html = outputLines.join('\n');

  // Bullet lists
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Code blocks
  html = html.replace(/```(\w*)\n([\s\S]+?)```/g, '<pre><code class="language-$1">$2</code></pre>');

  // Paragraphs
  html = html
    .split('\n\n')
    .map((p) => {
      if (p.startsWith('<') || p.trim() === '') return p;
      return `<p>${p}</p>`;
    })
    .join('\n');

  return html;
}
